import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Palette, Upload, Crop } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { API } from '../utils/api';
import { useBranding } from '../contexts/BrandingContext';
import LogoCropperModal from '../components/LogoCropperModal';

const BrandingSettings = () => {
  const { refresh: refreshGlobalBranding } = useBranding();
  const [branding, setBranding] = useState({
    app_name: '', primary_color: '#0f2a5a', accent_color: '#c9a24a',
    sidebar_text_color: '#ffffff', heading_color: '#0f2a5a',
    footer_text: '', logo_url: '', logo_height: 48, logo_fit: 'contain',
    narration_voice: 'nova'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropSource, setCropSource] = useState(null); // { src, mimeType }

  useEffect(() => {
    axios.get(`${API}/branding`).then(r => { setBranding(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/branding`, branding);
      setBranding(r.data);
      await refreshGlobalBranding();
      toast.success('Branding applied across the app.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  // Step 1: user picks a file → open the cropper (SVG files skip cropping — vector).
  const handleFilePick = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2 MB');
      return;
    }
    if (file.type === 'image/svg+xml') {
      // SVGs are vector — upload as-is, no cropping needed.
      uploadCroppedBlob(file, file.type);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropSource({ src: reader.result, mimeType: file.type || 'image/png' });
    };
    reader.readAsDataURL(file);
  };

  // Step 2: cropper returns a Blob → upload it.
  const uploadCroppedBlob = async (blob, mimeType) => {
    setUploading(true);
    setCropSource(null);
    try {
      const ext = (mimeType || '').split('/')[1] || 'png';
      const fd = new FormData();
      fd.append('file', blob, `logo.${ext === 'svg+xml' ? 'svg' : ext}`);
      const r = await axios.post(`${API}/branding/upload-logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBranding(prev => ({ ...prev, logo_url: r.data.logo_url }));
      toast.success('Logo uploaded. Click Save to apply.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally { setUploading(false); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="animate-fadeIn" data-testid="branding-settings-page">
      <div className="page-header">
        <h1 className="page-title"><Palette className="inline w-6 h-6 mr-2" /> Branding Settings</h1>
        <p className="page-subtitle">Customize your app's look — logo, colors, name (Super Admin only)</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}>
        {/* Form */}
        <div className="form-card">
          <div style={{ padding: 20 }}>
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <Label>App Name</Label>
                <Input value={branding.app_name || ''} onChange={(e) => setBranding({ ...branding, app_name: e.target.value })} data-testid="branding-app-name" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <Label>Primary Color <span style={{ fontSize: 10, color: '#888' }}>(sidebar bg, buttons)</span></Label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="color" value={branding.primary_color || '#0f2a5a'}
                           onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                           style={{ width: 44, height: 36, border: '1px solid #ddd', borderRadius: 6 }} data-testid="branding-primary-color" />
                    <Input value={branding.primary_color || ''} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Accent Color <span style={{ fontSize: 10, color: '#888' }}>(highlights, active nav)</span></Label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="color" value={branding.accent_color || '#c9a24a'}
                           onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                           style={{ width: 44, height: 36, border: '1px solid #ddd', borderRadius: 6 }} data-testid="branding-accent-color" />
                    <Input value={branding.accent_color || ''} onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })} />
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <Label>Sidebar Text Color <span style={{ fontSize: 10, color: '#888' }}>(nav labels)</span></Label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="color" value={branding.sidebar_text_color || '#ffffff'}
                           onChange={(e) => setBranding({ ...branding, sidebar_text_color: e.target.value })}
                           style={{ width: 44, height: 36, border: '1px solid #ddd', borderRadius: 6 }} data-testid="branding-sidebar-text" />
                    <Input value={branding.sidebar_text_color || ''} onChange={(e) => setBranding({ ...branding, sidebar_text_color: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Page Heading Color <span style={{ fontSize: 10, color: '#888' }}>(titles, H1s)</span></Label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="color" value={branding.heading_color || '#0f2a5a'}
                           onChange={(e) => setBranding({ ...branding, heading_color: e.target.value })}
                           style={{ width: 44, height: 36, border: '1px solid #ddd', borderRadius: 6 }} data-testid="branding-heading-color" />
                    <Input value={branding.heading_color || ''} onChange={(e) => setBranding({ ...branding, heading_color: e.target.value })} />
                  </div>
                </div>
              </div>
              <div>
                <Label>Footer Text</Label>
                <Input value={branding.footer_text || ''} onChange={(e) => setBranding({ ...branding, footer_text: e.target.value })} placeholder="© Company Name" data-testid="branding-footer" />
              </div>
              <div>
                <Label>Logo</Label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={(e) => handleFilePick(e.target.files?.[0])}
                         style={{ display: 'none' }} id="logo-upload-input" data-testid="branding-logo-input" />
                  <Button variant="outline" onClick={() => document.getElementById('logo-upload-input').click()} disabled={uploading}>
                    {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Upload New Logo
                  </Button>
                  {branding.logo_url && !branding.logo_url.startsWith('data:image/svg') && (
                    <Button variant="outline" onClick={() => setCropSource({ src: branding.logo_url, mimeType: 'image/png' })}
                            disabled={uploading} data-testid="branding-recrop-btn">
                      <Crop className="w-4 h-4 mr-2" />
                      Re-crop / Reposition
                    </Button>
                  )}
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>PNG/JPG/SVG/WebP · max 2 MB · crop opens after upload</span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, alignItems: 'end' }}>
                <div>
                  <Label>Logo Height <span style={{ fontSize: 11, color: '#888' }}>({branding.logo_height || 48}px)</span></Label>
                  <input type="range" min="24" max="96" step="2" value={branding.logo_height || 48}
                         onChange={(e) => setBranding({ ...branding, logo_height: parseInt(e.target.value, 10) })}
                         style={{ width: '100%' }} data-testid="branding-logo-height" />
                </div>
                <div>
                  <Label>Logo Fit</Label>
                  <select value={branding.logo_fit || 'contain'}
                          onChange={(e) => setBranding({ ...branding, logo_fit: e.target.value })}
                          style={{ width: '100%', height: 36, border: '1px solid #ddd', borderRadius: 6, padding: '0 8px', background: 'white' }}
                          data-testid="branding-logo-fit">
                    <option value="contain">Contain (preserve ratio)</option>
                    <option value="cover">Cover (fill area)</option>
                  </select>
                </div>
              </div>

              <div>
                <Label>Onboarding Narration Voice <span style={{ fontSize: 10, color: '#888' }}>(AI voice for the User Guide walkthrough)</span></Label>
                <select value={branding.narration_voice || 'nova'}
                        onChange={(e) => setBranding({ ...branding, narration_voice: e.target.value })}
                        style={{ width: '100%', height: 36, border: '1px solid #ddd', borderRadius: 6, padding: '0 8px', background: 'white' }}
                        data-testid="branding-narration-voice">
                  <option value="alloy">Alloy — Neutral, balanced</option>
                  <option value="ash">Ash — Clear, articulate</option>
                  <option value="coral">Coral — Warm, friendly</option>
                  <option value="echo">Echo — Smooth, calm</option>
                  <option value="fable">Fable — Expressive, storytelling</option>
                  <option value="nova">Nova — Energetic, upbeat (default)</option>
                  <option value="onyx">Onyx — Deep, authoritative</option>
                  <option value="sage">Sage — Wise, measured</option>
                  <option value="shimmer">Shimmer — Bright, cheerful</option>
                </select>
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                  Changing the voice clears the audio cache; new clips are auto-generated next time someone opens the User Guide (~5–10s).
                </div>
              </div>
              <Button onClick={handleSave} disabled={saving} data-testid="branding-save-btn"
                      style={{ backgroundColor: branding.primary_color || '#0f2a5a', color: 'white', width: 'fit-content' }}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {saving ? 'Saving...' : 'Save Branding'}
              </Button>
            </div>
          </div>
        </div>

        {/* Live Preview */}
        <div className="form-card">
          <div style={{ padding: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 12 }}>LIVE PREVIEW</div>
            <div style={{
              padding: 20, borderRadius: 12,
              background: branding.primary_color || '#0f2a5a',
              color: branding.sidebar_text_color || '#fff', textAlign: 'center'
            }}>
              {branding.logo_url && (
                <img src={branding.logo_url} alt="Logo" style={{
                  height: `${branding.logo_height || 48}px`, maxWidth: '160px',
                  marginBottom: 12, objectFit: branding.logo_fit || 'contain',
                  display: 'block', margin: '0 auto 12px',
                }} />
              )}
              <div style={{ fontSize: 16, fontWeight: 600 }}>{branding.app_name || 'Your App'}</div>
              <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>Sidebar nav item — muted text</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>{branding.footer_text || '© Company'}</div>
              <button style={{
                marginTop: 16, background: branding.accent_color, color: '#111', border: 'none',
                padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
              }}>Sample Accent Button</button>
            </div>
            <div style={{ marginTop: 12, padding: 12, background: '#f8f9fa', borderRadius: 8 }}>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 600, color: branding.heading_color || '#0f2a5a' }}>
                Sample Page Title
              </div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>This uses your heading color</div>
            </div>
          </div>
        </div>
      </div>
      {cropSource && (
        <LogoCropperModal
          imageSrc={cropSource.src}
          mimeType={cropSource.mimeType}
          onCancel={() => setCropSource(null)}
          onConfirm={(blob) => uploadCroppedBlob(blob, cropSource.mimeType)}
        />
      )}
    </div>
  );
};

export default BrandingSettings;
